import {
  EncodedPacket,
  EncodedVideoPacketSource,
  Mp4OutputFormat,
  Output
} from 'mediabunny';
import {
  getRenderAbortError,
  throwIfRenderAborted,
  waitForRenderOperation
} from './RenderTask';
import {
  estimateVideoExportBytes,
  prepareVideoExportStorage,
  type PreparedVideoExportStorage,
  type VideoExportArtifact,
  type VideoExportStorageKind
} from './videoExportStorage';

const DEFAULT_PENDING_PACKET_LIMIT = 4;
const DEFAULT_PACKET_STALL_TIMEOUT_MS = 45_000;
const FINALIZE_TIMEOUT_MS = 120_000;

type ProgressWaiter = {
  resolve: () => void;
  reject: (error: Error) => void;
  cleanup: () => void;
};

export interface StreamingMp4MuxerOptions {
  totalFrames: number;
  frameRate: number;
  bitrate: number;
  durationSeconds: number;
  outputFileHandle?: FileSystemFileHandle | null;
  signal?: AbortSignal;
  pendingPacketLimit?: number;
  onError?: (error: Error) => void;
}

export class StreamingMp4Muxer {
  readonly storageKind: VideoExportStorageKind;

  private readonly source = new EncodedVideoPacketSource('avc');
  private readonly output: Output<Mp4OutputFormat>;
  private readonly progressWaiters = new Set<ProgressWaiter>();
  private writeTail: Promise<void> = Promise.resolve();
  private pendingWrites = 0;
  private packetCount = 0;
  private failure: Error | null = null;
  private finalized = false;
  private cancelPromise: Promise<void> | null = null;

  private constructor(
    private readonly storage: PreparedVideoExportStorage,
    private readonly options: StreamingMp4MuxerOptions
  ) {
    this.storageKind = storage.storageKind;
    this.output = new Output({
      format: new Mp4OutputFormat({ fastStart: 'reserve' }),
      target: storage.target
    });
    this.output.addVideoTrack(this.source, {
      frameRate: options.frameRate,
      maximumPacketCount: options.totalFrames
    });
    options.signal?.addEventListener('abort', this.handleAbort, { once: true });
  }

  static async create(options: StreamingMp4MuxerOptions): Promise<StreamingMp4Muxer> {
    validateOptions(options);
    throwIfRenderAborted(options.signal);

    const storage = await prepareVideoExportStorage({
      estimatedBytes: estimateVideoExportBytes(options.bitrate, options.durationSeconds),
      outputFileHandle: options.outputFileHandle,
      signal: options.signal
    });
    const muxer = new StreamingMp4Muxer(storage, options);
    try {
      await waitForRenderOperation(muxer.output.start(), {
        signal: options.signal,
        label: 'MP4 stream initialization'
      });
      return muxer;
    } catch (error) {
      muxer.fail(error);
      await muxer.cancel();
      throw muxer.failure!;
    }
  }

  enqueueVideoChunk(
    chunk: EncodedVideoChunk,
    metadata?: EncodedVideoChunkMetadata
  ): void {
    this.throwIfUnavailable();
    if (this.finalized) {
      throw new Error('Cannot add video packets after MP4 finalization.');
    }
    if (this.packetCount === 0 && !metadata?.decoderConfig) {
      const error = new Error('The first H.264 packet is missing its decoder configuration.');
      this.fail(error);
      throw error;
    }
    if (this.packetCount >= this.options.totalFrames) {
      const error = new Error(
        `The encoder produced more packets than expected (${this.options.totalFrames}).`
      );
      this.fail(error);
      throw error;
    }

    const packet = EncodedPacket.fromEncodedChunk(chunk);
    this.packetCount += 1;
    this.pendingWrites += 1;

    const writeOperation = this.writeTail.then(async () => {
      this.throwIfUnavailable();
      await waitForRenderOperation(this.source.add(packet, metadata), {
        signal: this.options.signal,
        label: 'MP4 packet write',
        timeoutMs: DEFAULT_PACKET_STALL_TIMEOUT_MS
      });
    });

    this.writeTail = writeOperation
      .catch((error) => {
        this.fail(error);
      })
      .finally(() => {
        this.pendingWrites = Math.max(0, this.pendingWrites - 1);
        this.notifyWriteProgress();
      });
  }

  async waitForCapacity(): Promise<void> {
    this.throwIfUnavailable();
    const limit = this.options.pendingPacketLimit ?? DEFAULT_PENDING_PACKET_LIMIT;
    while (this.pendingWrites > limit) {
      await this.waitForWriteProgress();
      this.throwIfUnavailable();
    }
  }

  async finalize(): Promise<VideoExportArtifact> {
    this.throwIfUnavailable();
    await waitForRenderOperation(this.writeTail, {
      signal: this.options.signal,
      label: 'MP4 packet queue drain',
      timeoutMs: DEFAULT_PACKET_STALL_TIMEOUT_MS
    });
    this.throwIfUnavailable();

    if (this.packetCount !== this.options.totalFrames) {
      throw new Error(
        `The encoder produced ${this.packetCount} of ${this.options.totalFrames} expected packets.`
      );
    }

    const finalization = this.output.finalize();
    try {
      await waitForRenderOperation(finalization, {
        signal: this.options.signal,
        label: 'MP4 stream finalization',
        timeoutMs: FINALIZE_TIMEOUT_MS
      });
      throwIfRenderAborted(this.options.signal);
      const artifact = await this.storage.finalizeArtifact();
      this.finalized = true;
      this.removeAbortListener();
      console.info(
        `[VideoExport] MP4 finalized via ${artifact.storageKind} (${artifact.size} bytes).`
      );
      return artifact;
    } catch (error) {
      this.fail(error);
      void finalization
        .catch(() => {
          // Preserve the first failure reported to the user.
        })
        .finally(() => this.storage.discard());
      await this.storage.discard();
      throw this.failure!;
    }
  }

  async cancel(): Promise<void> {
    if (this.finalized) {
      return;
    }
    if (this.cancelPromise) {
      return this.cancelPromise;
    }

    this.cancelPromise = (async () => {
      this.removeAbortListener();
      try {
        await this.output.cancel();
      } catch (error) {
        this.fail(error);
      } finally {
        await this.storage.discard();
        this.notifyWriteProgress();
      }
    })();
    return this.cancelPromise;
  }

  private readonly handleAbort = (): void => {
    this.fail(getRenderAbortError(this.options.signal));
    void this.cancel();
  };

  private removeAbortListener(): void {
    this.options.signal?.removeEventListener('abort', this.handleAbort);
  }

  private throwIfUnavailable(): void {
    throwIfRenderAborted(this.options.signal);
    if (this.failure) {
      throw this.failure;
    }
  }

  private fail(error: unknown): void {
    if (this.failure) {
      return;
    }
    this.failure = error instanceof Error ? error : new Error(String(error));
    this.options.onError?.(this.failure);
    const waiters = Array.from(this.progressWaiters);
    this.progressWaiters.clear();
    waiters.forEach((waiter) => {
      waiter.cleanup();
      waiter.reject(this.failure!);
    });
  }

  private notifyWriteProgress(): void {
    const waiters = Array.from(this.progressWaiters);
    this.progressWaiters.clear();
    waiters.forEach((waiter) => {
      waiter.cleanup();
      waiter.resolve();
    });
  }

  private waitForWriteProgress(): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(
        () => finishReject(new Error('MP4 streaming output stopped responding.')),
        DEFAULT_PACKET_STALL_TIMEOUT_MS
      );
      const onAbort = () => finishReject(getRenderAbortError(this.options.signal));
      const cleanup = () => {
        window.clearTimeout(timeoutId);
        this.options.signal?.removeEventListener('abort', onAbort);
      };
      const waiter: ProgressWaiter = {
        resolve: () => {
          cleanup();
          resolve();
        },
        reject: (error) => {
          cleanup();
          reject(error);
        },
        cleanup
      };
      const finishReject = (error: Error) => {
        this.progressWaiters.delete(waiter);
        waiter.reject(error);
      };

      this.options.signal?.addEventListener('abort', onAbort, { once: true });
      this.progressWaiters.add(waiter);
      if (this.failure) {
        finishReject(this.failure);
      }
    });
  }
}

function validateOptions(options: StreamingMp4MuxerOptions): void {
  if (!Number.isInteger(options.totalFrames) || options.totalFrames <= 0) {
    throw new TypeError('totalFrames must be a positive integer.');
  }
  if (!Number.isFinite(options.frameRate) || options.frameRate <= 0) {
    throw new TypeError('frameRate must be a positive number.');
  }
  if (!Number.isFinite(options.bitrate) || options.bitrate <= 0) {
    throw new TypeError('bitrate must be a positive number.');
  }
  if (!Number.isFinite(options.durationSeconds) || options.durationSeconds <= 0) {
    throw new TypeError('durationSeconds must be a positive number.');
  }
  if (
    options.pendingPacketLimit !== undefined
    && (!Number.isInteger(options.pendingPacketLimit) || options.pendingPacketLimit < 1)
  ) {
    throw new TypeError('pendingPacketLimit must be a positive integer.');
  }
}
